import { useState, useEffect, useMemo } from 'react';
import { DataService } from '../services/data-service';
import { buildClientIdentity } from '../services/client-identity-match';
import { assessStage3WorkMethod, isStage3StaleOnly } from '../utils/stage3-work-method';
import { inferCompletedStages, isQuoteBrainQuote } from '../utils/project-stage';
import { QUOTE_BRAIN_NOT_RUN_STAGES } from '../utils/quote-brain-project';
import { isStage3StaleHardBlock } from '../utils/feature-flags';

export interface UseStageCompletionAssessmentParams {
    stage: number;
    currentProjectId: string | null;
    projects: any[];
    formData: any;
    photos: any[];
    documents: any[];
    videos: any[];
    projectSummary: any;
    analysis: any;
    photoAnalysis: any;
    projectWMS: any;
    projectStrategy: any;
    scope: any[];
    quoteData: any;
    completedStages: any;
    /**
     * The project's workflow mode (Full AI or Fast-Track manual scope). Completion truth depends on it (5B / O-F11): a Fast-Track
     * route does not perform Risk or Method, so scope or a quote on its own must not show them complete. Absent reads as no mode.
     */
    workflowMode?: string | null;
}

export interface UseStageCompletionAssessmentResult {
    clientIdentity: any;
    stage3MethodAssessment: any;
    effectiveCompletedStages: Set<number>;
    /** On a Quote Brain job, the stages the brain did not do (Risks, Method) with the pill's explanation; null otherwise. */
    notRunStages: Readonly<Record<number, string>> | null;
}

export function useStageCompletionAssessment({
    stage,
    currentProjectId,
    projects,
    formData,
    photos,
    documents,
    videos,
    projectSummary,
    analysis,
    photoAnalysis,
    projectWMS,
    projectStrategy,
    scope,
    quoteData,
    completedStages,
    workflowMode = null,
}: UseStageCompletionAssessmentParams): UseStageCompletionAssessmentResult {
    // Only Stage 1 shows the identity (the CRM lines on its Requester and Site cards). Stage 2 has no identity card, so it
    // fetches nothing.
    const active = stage === 1;
    const project = useMemo<any>(() => (active && currentProjectId
        ? (projects.find((p: any) => p.id === currentProjectId) || { id: currentProjectId })
        : null), [active, currentProjectId, projects]);
    const companyId = project ? (project.company_id ?? project.companyId ?? null) : null;
    const propertyId = project ? (project.property_id ?? project.propertyId ?? null) : null;
    const crmKey = project ? `${currentProjectId}|${companyId ?? ''}|${propertyId ?? ''}` : null;

    // The CRM company and site are fetched only when the job, its company or its
    // site changes (SPEC K5). Typing on Stage 1 changes formData on every
    // keystroke, and an autosave replaces `projects`; neither refetches.
    const [crmRecords, setCrmRecords] = useState<{ key: string; company: any; property: any } | null>(null);
    useEffect(() => {
        let cancelled = false;
        let disabled = false;
        try { disabled = localStorage.getItem('qm_disable_client_identity_card') === '1'; } catch { /* ignore */ }
        if (!crmKey || disabled) { setCrmRecords(null); return; }
        (async () => {
            let company = null, property = null;
            try { if (companyId) company = await DataService.companies.getById(companyId); } catch { /* non-fatal */ }
            try { if (propertyId) property = await DataService.properties.getById(propertyId); } catch { /* non-fatal */ }
            if (cancelled) return;
            setCrmRecords({ key: crmKey, company, property });
        })();
        return () => { cancelled = true; };
    }, [crmKey, companyId, propertyId]);

    // The identity itself is worked out from the fetched records and the fields
    // buildClientIdentity reads, with no request, so it follows the form as it is typed.
    const requesterCompany = formData?.requesterCompany;
    const clientCompany = formData?.clientCompany;
    const address = formData?.address;
    const suburb = formData?.suburb;
    const strataplan = formData?.strataplan;
    const strataPlan = formData?.strataPlan;
    const clientIdentity = useMemo(() => {
        if (!project || !crmRecords || crmRecords.key !== crmKey) return null;
        try {
            const identityForm = { requesterCompany, clientCompany, address, suburb, strataplan, strataPlan };
            return buildClientIdentity({
                project,
                formData: identityForm,
                company: crmRecords.company,
                property: crmRecords.property,
                allProjects: projects,
            });
        } catch {
            return null;
        }
    }, [project, crmRecords, crmKey, projects, requesterCompany, clientCompany, address, suburb, strataplan, strataPlan]);

    const stage3MethodAssessment = useMemo(() => assessStage3WorkMethod({
        wms: projectWMS,
        scope,
        strategy: projectStrategy,
    }), [projectWMS, scope, projectStrategy]);

    const effectiveCompletedStages = useMemo(() => {
        const inferred = inferCompletedStages({
            completedStages,
            currentStage: stage,
            formData,
            photos,
            documents,
            videos,
            projectSummary,
            riskAnalysis: analysis,
            photoAnalysis,
            wms: projectWMS,
            projectStrategy,
            scope,
            quoteData,
            workflowMode,
        });

        if (!stage3MethodAssessment.ready
            && !(!isStage3StaleHardBlock() && isStage3StaleOnly(stage3MethodAssessment))) {
            inferred.delete(3);
        }
        return inferred;
    }, [
        completedStages,
        stage,
        formData,
        photos,
        documents,
        videos,
        projectSummary,
        analysis,
        photoAnalysis,
        projectWMS,
        projectStrategy,
        scope,
        quoteData,
        workflowMode,
        stage3MethodAssessment,
    ]);

    const brainQuote = isQuoteBrainQuote({ scope, quoteData });
    const notRunStages = useMemo(() => {
        if (!brainQuote) return null;
        const entries = Object.entries(QUOTE_BRAIN_NOT_RUN_STAGES).filter(([id]) => !effectiveCompletedStages.has(Number(id)));
        return entries.length ? Object.fromEntries(entries) : null;
    }, [brainQuote, effectiveCompletedStages]);

    return {
        clientIdentity,
        stage3MethodAssessment,
        effectiveCompletedStages,
        notRunStages,
    };
}
